import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../store/redux/hooks';

/** Fields to exclude from dirty comparison (UI-only state). */
const IGNORED_KEYS = new Set([
  'focusedField',
  'previewLanguage',
  '_isCreating',
  '_formCreateSuccessId',
  '_formWasDisabledOnLoad',
  '_fromPreset',
  '_appleEnhancedDesignOnLoad',
  'originalDefaultValues',
  '_passesCount',
]);

function getComparableState(state: Record<string, unknown>): string {
  const filtered: Record<string, unknown> = {};
  for (const key of Object.keys(state)) {
    if (!IGNORED_KEYS.has(key)) {
      filtered[key] = state[key];
    }
  }
  return JSON.stringify(filtered);
}

export function useUnsavedChanges(enabled: boolean) {
  const templateState = useAppSelector((s) => s.template) as unknown as Record<
    string,
    unknown
  >;
  const isCreating = useAppSelector((s) => s.template._isCreating);
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const pendingAnchorRef = useRef<HTMLElement | null>(null);
  const bypassRef = useRef(false);
  const proceedingRef = useRef(false);
  const prevEnabledRef = useRef(false);
  const snapshotTakenRef = useRef(false);
  const templateStateRef = useRef(templateState);
  templateStateRef.current = templateState;

  useEffect(() => {
    if (enabled && !snapshotTakenRef.current) {
      snapshotTakenRef.current = true;
      const id = setTimeout(() => {
        const snap = getComparableState(templateStateRef.current);
        setSnapshot(snap);
      }, 1000);
      return () => {
        if (!enabled) clearTimeout(id);
      };
    }
    if (!enabled) {
      snapshotTakenRef.current = false;
      setSnapshot(null);
    }
    prevEnabledRef.current = enabled;
  }, [enabled]);

  const [isDirty, setIsDirty] = useState(false);
  const dirtyTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!enabled || isCreating || snapshot === null) {
      setIsDirty(false);
      return;
    }
    clearTimeout(dirtyTimerRef.current);
    dirtyTimerRef.current = setTimeout(() => {
      setIsDirty(getComparableState(templateStateRef.current) !== snapshot);
    }, 500);
    return () => clearTimeout(dirtyTimerRef.current);
  }, [enabled, isCreating, snapshot, templateState]);

  // Block browser close/refresh
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Intercept link clicks within the app
  useEffect(() => {
    if (!isDirty) return;

    const handleClick = (e: MouseEvent) => {
      if (bypassRef.current) {
        bypassRef.current = false;
        return;
      }
      const anchor = (e.target as HTMLElement).closest('a[href]');
      if (!anchor) return;
      if ((anchor as HTMLAnchorElement).target === '_blank') return;
      const rawHref = anchor.getAttribute('href');
      if (!rawHref || rawHref.startsWith('mailto:')) return;
      let path = rawHref;
      try {
        const url = new URL(rawHref, window.location.origin);
        if (url.origin !== window.location.origin) return;
        path = url.pathname + url.search + url.hash;
      } catch {
        // Preserve the raw value so the blocked navigation can still be resumed.
      }

      e.preventDefault();
      e.stopPropagation();
      pendingAnchorRef.current = anchor as HTMLElement;
      setPendingPath(path);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isDirty]);

  // Intercept browser back/forward button
  useEffect(() => {
    if (!isDirty) return;

    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (proceedingRef.current) {
        proceedingRef.current = false;
        return;
      }
      window.history.pushState(null, '', window.location.href);
      setPendingPath('__back__');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty]);

  const proceed = useCallback(() => {
    if (pendingPath) {
      const path = pendingPath;
      const anchor = pendingAnchorRef.current;
      setPendingPath(null);
      pendingAnchorRef.current = null;
      if (path === '__back__') {
        proceedingRef.current = true;
        window.history.go(-2);
      } else {
        const el =
          anchor && anchor.isConnected
            ? anchor
            : document.querySelector(`a[href="${path}"]`);
        if (el) {
          bypassRef.current = true;
          (el as HTMLElement).click();
        } else {
          navigate(path);
        }
      }
    }
  }, [pendingPath, navigate]);

  const reset = useCallback(() => {
    setPendingPath(null);
    pendingAnchorRef.current = null;
  }, []);

  const markClean = useCallback(() => {
    setSnapshot(getComparableState(templateStateRef.current));
  }, []);

  return {
    isBlocked: pendingPath !== null,
    proceed,
    reset,
    markClean,
  };
}
